import React from 'react'

import { useTranslation } from 'react-i18next'

import { Form as FormikForm, Formik, Field, FieldProps } from 'formik'
import { i18n as i18nType } from 'i18next'

import { ConsentsFormProps, Form, LayoutItem, Optins } from '../../../ana-toolbox'
import { CheckboxField } from '../../components/checkbox-field/checkbox-field'
import { FormLayout } from '../../components/form-layout/form-layout'
import { SubmitButton } from '../../components/submit-button/submit-button'
import { Translate } from '../../components/translate/translate'
import consentsFields from '../../utils/fields/consents'
import { createFields } from '../../utils/helpers/createFields'
import { getFormInitialErrors } from '../../utils/helpers/getFormInitialErrors'
import { buildValidationSchema } from '../../utils/yup/buildValidationSchema'
import { combinedFields } from '../../utils/yup/main'

export const consentsLayout = ({
  clinicReferenceId,
  anaBackendUrl,
  offerInsurance,
  optins,
  i18n,
  privacyPolicyUrl = `https://assistant.patient21.com/${i18n.language}/privacy`,
}: {
  privacyPolicyUrl?: string
  clinicReferenceId?: string
  anaBackendUrl?: string
  offerInsurance?: boolean
  optins: Optins
  i18n: i18nType
}): LayoutItem[] => [
  {
    type: 'custom',
    xs: 12,
    key: combinedFields.confidentialityAgreementAccepted.name,
    render: () => {
      const field = combinedFields.confidentialityAgreementAccepted

      if (optins.confidentialityAgreementAccepted) return null

      return (
        <CheckboxField
          name={field.name}
          label={
            <Translate
              options={{ postProcess: 'markdown' }}
              fileUrl={
                clinicReferenceId && anaBackendUrl
                  ? `${anaBackendUrl}/clinic/${clinicReferenceId}/waiver-of-confidentiality.pdf?language=${i18n.language}`
                  : ''
              }
            >
              {field.label}
            </Translate>
          }
        />
      )
    },
  },
  {
    type: 'custom',
    xs: 12,
    key: combinedFields.insuranceConsentAccepted.name,
    render: () => {
      const field = combinedFields.insuranceConsentAccepted

      if (!offerInsurance) return null

      return (
        <CheckboxField
          name={field.name}
          label={
            <Translate
              dangerous
              privadentUrl="https://www.privadent.de/datenschutzerklaerung/"
              dzvsUrl="https://dzvs.de/datenschutzerklaerung/"
            >
              {field.label}
            </Translate>
          }
        />
      )
    },
  },
  {
    type: 'custom',
    xs: 12,
    key: combinedFields.marketingCommunicationConsentAccepted.name,
    render: () => {
      const field = combinedFields.marketingCommunicationConsentAccepted

      if (optins.marketingCommunicationConsentAccepted) return null

      return (
        <CheckboxField
          name={field.name}
          label={
            <Translate dangerous url={privacyPolicyUrl}>
              {field.label}
            </Translate>
          }
        />
      )
    },
  },
]

const getConsentInitialValue = (value: unknown): boolean => {
  if (value === true || value === false) {
    return value
  }

  return true
}

export const ConsentsForm = ({
  onSubmit,
  innerRef,
  initialValues = {},
  loading,
  portalAttr,
  offerInsurance,
  clinicReferenceId,
  anaBackendUrl,
  privacyPolicyUrl,
  optins = {
    dataProtectionAgreementAccepted: false,
    patientInformationAccepted: false,
    confidentialityAgreementAccepted: false,
    marketingCommunicationConsentAccepted: false,
    tosPatientAccountAccepted: false,
  },
}: ConsentsFormProps) => {
  const { i18n } = useTranslation()

  const formInitialValues: Partial<Form> = {
    ...initialValues,
    confidentialityAgreementAccepted: getConsentInitialValue(initialValues.confidentialityAgreementAccepted),
    marketingCommunicationConsentAccepted: getConsentInitialValue(initialValues.marketingCommunicationConsentAccepted),
  }

  const layout = consentsLayout({
    clinicReferenceId,
    anaBackendUrl,
    i18n,
    optins,
    offerInsurance,
    privacyPolicyUrl,
  })

  const handleSubmit = (values: Partial<Form>) => {
    onSubmit({ ...values })
  }

  const submitButton = (
    <Field>
      {(props: FieldProps) => {
        return (
          <SubmitButton onClick={() => props.form.submitForm()} disabled={!props.form.isValid} loading={loading}>
            <Translate>next</Translate>
          </SubmitButton>
        )
      }}
    </Field>
  )

  const consentsValidationSchema = buildValidationSchema(createFields(consentsFields))

  return (
    <Formik
      innerRef={innerRef}
      initialValues={formInitialValues}
      validationSchema={consentsValidationSchema}
      initialErrors={getFormInitialErrors(consentsValidationSchema, formInitialValues)}
      onSubmit={handleSubmit}
    >
      <FormikForm>
        <FormLayout portalAttr={portalAttr} items={layout} submitButton={submitButton} />
      </FormikForm>
    </Formik>
  )
}
